import { centreStorageKey } from '../tenant/centreStorage';

/** Legacy key (pre–centre-scoped); still read for migration. */
export const EMPLOYEE_GROUPE_STORAGE_KEY = 'garderie-employee-groupe-assignments';
export const EMPLOYEE_GROUPE_UPDATED_EVENT = 'garderie:employee-groupe-updated';

/**
 * Profile roster layout order:
 * A → B → C → D → E → F → Kitchen → Office → R (replacement) → G.
 * Multi-assign allowed; roster section = first code in this order.
 */
export const EMPLOYEE_GROUPE_CODES = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'Kitchen',
  'Office',
  'R',
  'G',
] as const;
export type EmployeeGroupeCode = (typeof EMPLOYEE_GROUPE_CODES)[number];

/** Visible roster section order. */
export const ROSTER_GROUPE_ORDER: readonly EmployeeGroupeCode[] = EMPLOYEE_GROUPE_CODES;

export const SPECIAL_NEEDS_GROUPE_CODE: EmployeeGroupeCode = 'G';
export const REPLACEMENT_GROUPE_CODE: EmployeeGroupeCode = 'R';

/** Educator groups shown by default in shift management (A→F). */
export const SHIFT_EDUCATOR_GROUPE_CODES = ['A', 'B', 'C', 'D', 'E', 'F'] as const;
export type ShiftEducatorGroupeCode = (typeof SHIFT_EDUCATOR_GROUPE_CODES)[number];

export function isShiftEducatorGroupe(
  value: string | null | undefined
): value is ShiftEducatorGroupeCode {
  const code = normalizeEmployeeGroupeCode(value);
  return (
    code != null &&
    (SHIFT_EDUCATOR_GROUPE_CODES as readonly string[]).includes(code)
  );
}

/** Primary roster group is A–F, or R when extra personnel (replacements) are included. Unassigned is excluded. */
export function isShiftPoolGroupe(
  value: string | null | undefined,
  includeReplacement: boolean
): boolean {
  if (value == null || value === '') return false;
  if (isShiftEducatorGroupe(value)) return true;
  return includeReplacement && isReplacementGroupe(value);
}

/**
 * Year-scoped group eligibility (physical capability), not shift placement.
 * An employee may be able to work in D/E/F but not A/B/C (e.g. mobility).
 */
export interface EmployeeGroupeAssignment {
  employeeId: string;
  year: number;
  /** @deprecated Prefer `allowedGroupes`. Still read for migration. */
  groupeName?: string;
  /** Groups this employee can physically work in. Empty = unassigned. */
  allowedGroupes?: EmployeeGroupeCode[];
}

/** Demo defaults until HR assigns groups in the Employees UI. */
const SEED_GROUPE_BY_EMPLOYEE: Record<string, EmployeeGroupeCode> = {
  'teacher-1': 'A',
  'teacher-2': 'A',
  'teacher-3': 'B',
  'teacher-4': 'C',
  'teacher-5': 'D',
  'teacher-6': 'E',
  'teacher-7': 'F',
  'teacher-8': 'G',
};

const LEGACY_NAME_TO_CODE: Record<string, EmployeeGroupeCode> = {
  'groupe poupons': 'A',
  'groupe explorateurs': 'B',
  'groupe aventuriers': 'C',
  'groupe découvreurs': 'D',
  'groupe decouvreurs': 'D',
  'special needs': 'G',
  'besoins particuliers': 'G',
  kitchen: 'Kitchen',
  cuisine: 'Kitchen',
  office: 'Office',
  bureau: 'Office',
  admin: 'Office',
  replacement: 'R',
  remplaçant: 'R',
  remplacant: 'R',
  floater: 'R',
  flottant: 'R',
};

function storageKey(): string {
  return centreStorageKey('employee-groupe-assignments');
}

function readAll(): EmployeeGroupeAssignment[] {
  if (typeof window === 'undefined') return [];
  try {
    const scoped = localStorage.getItem(storageKey());
    if (scoped) return JSON.parse(scoped) as EmployeeGroupeAssignment[];
    const legacy = localStorage.getItem(EMPLOYEE_GROUPE_STORAGE_KEY);
    if (!legacy) return [];
    const rows = JSON.parse(legacy) as EmployeeGroupeAssignment[];
    writeAll(rows);
    return rows;
  } catch {
    return [];
  }
}

function writeAll(rows: EmployeeGroupeAssignment[]): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(storageKey(), JSON.stringify(rows));
  window.dispatchEvent(new CustomEvent(EMPLOYEE_GROUPE_UPDATED_EVENT));
}

function sortCodes(codes: EmployeeGroupeCode[]): EmployeeGroupeCode[] {
  const order = new Map(EMPLOYEE_GROUPE_CODES.map((c, i) => [c, i]));
  return [...new Set(codes)].sort((a, b) => (order.get(a) ?? 99) - (order.get(b) ?? 99));
}

function codesFromRow(row: EmployeeGroupeAssignment | undefined): EmployeeGroupeCode[] | null {
  if (!row) return null;
  if (row.allowedGroupes) {
    return sortCodes(
      row.allowedGroupes
        .map((c) => normalizeEmployeeGroupeCode(c))
        .filter((c): c is EmployeeGroupeCode => c != null)
    );
  }
  const code = normalizeEmployeeGroupeCode(row.groupeName);
  return code ? [code] : [];
}

export function isEmployeeGroupeCode(value: string | null | undefined): value is EmployeeGroupeCode {
  if (!value) return false;
  return (EMPLOYEE_GROUPE_CODES as readonly string[]).some(
    (c) => c === value || c.toLowerCase() === value.toLowerCase()
  );
}

/** Map free-text / legacy names to roster group codes when possible. */
export function normalizeEmployeeGroupeCode(
  value: string | null | undefined
): EmployeeGroupeCode | null {
  if (!value?.trim()) return null;
  const trimmed = value.trim();
  const exact = EMPLOYEE_GROUPE_CODES.find(
    (c) => c === trimmed || c.toLowerCase() === trimmed.toLowerCase()
  );
  if (exact) return exact;
  return LEGACY_NAME_TO_CODE[trimmed.toLowerCase()] ?? null;
}

export function isSpecialNeedsGroupe(value: string | null | undefined): boolean {
  return normalizeEmployeeGroupeCode(value) === SPECIAL_NEEDS_GROUPE_CODE;
}

export function loadEmployeeGroupeAssignments(): EmployeeGroupeAssignment[] {
  return readAll();
}

/** Groups the employee can physically work in for the year (not shift rooms). */
export function getEmployeeAllowedGroupesForYear(
  employeeId: string,
  year: number
): EmployeeGroupeCode[] {
  const stored = readAll().find((r) => r.employeeId === employeeId && r.year === year);
  const codes = codesFromRow(stored);
  // Explicit row (including empty) means HR set or cleared — do not re-apply seed.
  if (codes !== null) return codes;
  const seed = SEED_GROUPE_BY_EMPLOYEE[employeeId];
  return seed ? [seed] : [];
}

export function setEmployeeAllowedGroupesForYear(
  employeeId: string,
  year: number,
  codes: EmployeeGroupeCode[]
): void {
  const all = readAll().filter((r) => !(r.employeeId === employeeId && r.year === year));
  const allowed = sortCodes(
    codes
      .map((c) => normalizeEmployeeGroupeCode(c))
      .filter((c): c is EmployeeGroupeCode => c != null)
  );
  all.push({
    employeeId,
    year,
    allowedGroupes: allowed,
    groupeName: allowed[0],
  });
  writeAll(all);
}

/** Clear every employee to unassigned for the year (overrides demo seeds). */
export function clearAllEmployeeGroupesForYear(
  year: number,
  employeeIds: string[]
): void {
  const kept = readAll().filter((r) => r.year !== year);
  for (const employeeId of employeeIds) {
    kept.push({ employeeId, year, allowedGroupes: [], groupeName: undefined });
  }
  writeAll(kept);
}

/** @deprecated Prefer getEmployeeAllowedGroupesForYear — returns first allowed code. */
export function getEmployeeGroupeForYear(employeeId: string, year: number): string | null {
  return getEmployeeAllowedGroupesForYear(employeeId, year)[0] ?? null;
}

/** @deprecated Prefer getEmployeeAllowedGroupesForYear — returns first allowed code. */
export function getEmployeeGroupeCodeForYear(
  employeeId: string,
  year: number
): EmployeeGroupeCode | null {
  return getEmployeeAllowedGroupesForYear(employeeId, year)[0] ?? null;
}

/**
 * Roster bucket: first assigned group in layout order (A…R…G).
 * Multi-group employees still appear only under that primary section.
 */
export function getEmployeeRosterGroupe(
  employeeId: string,
  year = currentAssignmentYear()
): EmployeeGroupeCode | null {
  const allowed = getEmployeeAllowedGroupesForYear(employeeId, year);
  return allowed[0] ?? null;
}

export function isReplacementGroupe(value: string | null | undefined): boolean {
  return normalizeEmployeeGroupeCode(value) === REPLACEMENT_GROUPE_CODE;
}

export function groupeSortIndex(code: EmployeeGroupeCode | null): number {
  if (!code) return ROSTER_GROUPE_ORDER.length;
  const i = ROSTER_GROUPE_ORDER.indexOf(code);
  return i >= 0 ? i : ROSTER_GROUPE_ORDER.length;
}

function employeeNameKey(employee: { lastName: string; firstName: string }): string {
  return `${employee.lastName} ${employee.firstName}`.toLocaleLowerCase('fr');
}

export type SortEmployeesForRosterOptions = {
  /**
   * Profile roster: show people without a group first so new / incomplete
   * hires are visible (Remuneration already lists everyone by qualification).
   */
  unassignedFirst?: boolean;
};

/**
 * Profile roster sort: optional Unassigned first, then A→F→Kitchen→Office→R→G.
 * Multi-assign: employee appears only under the first (layout-order) group.
 * Within a bucket, alphabetical by name.
 */
export function sortEmployeesForRoster<T extends { id: string; firstName: string; lastName: string }>(
  employees: T[],
  year = currentAssignmentYear(),
  options?: SortEmployeesForRosterOptions
): T[] {
  const unassignedFirst = options?.unassignedFirst === true;
  return [...employees].sort((a, b) => {
    const ga = getEmployeeRosterGroupe(a.id, year);
    const gb = getEmployeeRosterGroupe(b.id, year);
    const ia = ga == null && unassignedFirst ? -1 : groupeSortIndex(ga);
    const ib = gb == null && unassignedFirst ? -1 : groupeSortIndex(gb);
    if (ia !== ib) return ia - ib;
    return employeeNameKey(a).localeCompare(employeeNameKey(b), 'fr');
  });
}

/** Replace eligibility with a single group (or clear). Kept for older call sites. */
export function setEmployeeGroupeForYear(
  employeeId: string,
  year: number,
  groupeName: string | null
): void {
  const code = normalizeEmployeeGroupeCode(groupeName);
  setEmployeeAllowedGroupesForYear(employeeId, year, code ? [code] : []);
}

export function employeeHasSpecialNeedsAccess(employeeId: string, year: number): boolean {
  return getEmployeeAllowedGroupesForYear(employeeId, year).includes(SPECIAL_NEEDS_GROUPE_CODE);
}

export function formatEmployeeGroupeLabel(
  groupeName: string | null,
  unassignedLabel: string
): string {
  const code = normalizeEmployeeGroupeCode(groupeName);
  if (code) return code;
  return groupeName?.trim() || unassignedLabel;
}

export function formatAllowedGroupesLabel(
  codes: EmployeeGroupeCode[],
  unassignedLabel: string
): string {
  if (codes.length === 0) return unassignedLabel;
  // Primary (roster section) first — already layout-sorted.
  return codes.join(', ');
}

export function currentAssignmentYear(date = new Date()): number {
  return date.getFullYear();
}

export function subscribeToEmployeeGroupeUpdates(listener: () => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  window.addEventListener(EMPLOYEE_GROUPE_UPDATED_EVENT, listener);
  return () => window.removeEventListener(EMPLOYEE_GROUPE_UPDATED_EVENT, listener);
}
